import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Chronotype, HeatmapDataType } from '@demind-inc/core';
import { maxBy, upperFirst } from 'lodash';
import { HelpOutlineOutlined } from '@mui/icons-material';
import { ReactComponent as SleepIcon } from '../../../assets/onboarding/sleep_bubble.svg';
import { ReactComponent as PulseSleepIcon } from '../../../assets/onboarding/pulse_sleep.svg';

import {
  IdealCircadianData,
  OnboardingButton,
  OnboardingWrapper,
  ResultAutoSlideBox,
  energyResultSteps,
  CustomTooltip,
  CustomTooltipContent,
} from '../../../components';
import {
  EnergyCircadianGetRes,
  useAuthContext,
  useCircadianHeatmap,
  useDiagnoseChronotype,
  useOnboarding,
} from '../../../data-access';

import './EnergyPredictionResultPage.scss';

import { CircularProgress, Stack, Typography } from '@mui/material';
import { useBreakpoints } from '../../../utils';
import { chronotypeTooltipInfo } from '../../../components/DataView/constants';

const getCurrentResultStep = (currentResultStepNumber: number) => {
  return energyResultSteps.find((step) => step.step === currentResultStepNumber);
};

const getIdealCircadinHeatmap = () => {
  return (IdealCircadianData as EnergyCircadianGetRes).circadian as HeatmapDataType[];
};

const EnergyPredictionResultPage: React.FC = () => {
  const [chronotype, setChronotype] = useState<Chronotype | null>(null);
  const [currentResultStepNumber, setCurrentResultStepNumber] = useState<number>(1);

  const { isMobile } = useBreakpoints();
  const { currentOnboardingStep, goToNextStep, goToPreviousStep, goToSpecificStep } =
    useOnboarding();
  const { diagnoseChronotype, isPending: isDiagnosing } = useDiagnoseChronotype();
  const { user } = useAuthContext();
  const { circadianRhythmsByDate, isLoading: isFetchingCircadian } = useCircadianHeatmap({
    userId: user.userId,
    calendarIds: [],
    metricId: user.metricId!,
    dates: [dayjs().format('YYYY-MM-DD')],
    timezone: dayjs.tz.guess(),
    enableStressCombineToEnergy: true,
    forceEnabled: true,
  });

  const isLoading = isFetchingCircadian || isDiagnosing;

  useEffect(() => {
    if (!user || !user.metricId) {
      return;
    }

    // Diagnose chronotype
    const init = async () => {
      const detectedChronotype = await diagnoseChronotype({
        userId: user.userId,
        metricId: user.metricId!,
      });
      setChronotype(detectedChronotype as Chronotype);
    };

    init();
  }, [user]);

  const todayCircadianRhythm = useMemo(() => {
    if (!circadianRhythmsByDate.length) {
      return [];
    }

    return circadianRhythmsByDate[0].values ?? [];
  }, [circadianRhythmsByDate]);

  const currentResultStep = useMemo(() => {
    return getCurrentResultStep(currentResultStepNumber);
  }, [currentResultStepNumber]);

  const renderInfoBox = useCallback(
    (icon: React.ComponentType<{ className?: string }>, content: React.ReactNode) => (
      <Stack
        direction="row"
        alignItems={'center'}
        gap={1.5}
        className="energy-prediction-result__chronotype"
      >
        {React.createElement(icon, { className: 'energy-prediction-result__chronotype__icon' })}
        {typeof content === 'string' ? (
          <Typography variant="subtitle1" className="energy-prediction-result__chronotype__text">
            {content}
          </Typography>
        ) : (
          content
        )}
      </Stack>
    ),
    []
  );

  const renderExplanation = useCallback(() => {
    if (!currentResultStep?.explanation) return null;

    const skimmedCircadianRhythm = todayCircadianRhythm
      .filter(({ value }) => value > 0)
      .filter((_, idx) => idx % 4 === 0);
    const morningPeakData = maxBy(
      skimmedCircadianRhythm.filter(({ phase }) => phase === 'morning_high'),
      'value'
    );
    const eveningPeakData = maxBy(
      skimmedCircadianRhythm.filter(({ phase }) => phase === 'evening_high'),
      'value'
    );

    const peakTime = [morningPeakData?.y, eveningPeakData?.y]
      .filter(Boolean)
      .map((time) => dayjs(time).format('h:mma'));

    return renderInfoBox(
      PulseSleepIcon,
      typeof currentResultStep.explanation === 'function'
        ? currentResultStep.explanation(peakTime)
        : currentResultStep.explanation
    );
  }, [currentResultStep, todayCircadianRhythm, renderInfoBox]);

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="energy-prediction-result__loading">
          <CircularProgress />
          <Typography variant="h5">Predicting your energy rhythm..</Typography>
        </div>
      );
    }

    if (!currentResultStep) {
      return null;
    }

    const renderChronotype = () => {
      const chronotypeText = (
        <Typography variant="subtitle2" className="energy-prediction-result__chronotype__text">
          This is based on your circadian rhythm, dictated by sleep, light exposure, and stress.
        </Typography>
      );

      return renderInfoBox(SleepIcon, chronotypeText);
    };

    return (
      <div className="energy-prediction-result">
        <div className="energy-prediction-result__container">
          {currentResultStep?.children(
            currentResultStep.useIdealCircadian ? getIdealCircadinHeatmap() : todayCircadianRhythm
          )}

          {/* Desktop: explanation inside container */}
          {!!currentResultStep?.explanation && !isMobile && renderExplanation()}
        </div>
        {/* Mobile: explanation outside container with wrapper */}
        {!!currentResultStep?.explanation && isMobile && (
          <div className="energy-prediction-result__chronotype-wrapper">{renderExplanation()}</div>
        )}
      </div>
    );
  }, [
    isLoading,
    chronotype,
    todayCircadianRhythm,
    currentResultStep,
    isMobile,
    renderExplanation,
    renderInfoBox,
  ]);

  const renderButtons = useMemo(() => {
    const isFistStep = currentResultStep?.step === 1;

    const buttons: OnboardingButton[] = [
      {
        text: isFistStep ? 'Learn more about my energy' : 'Plan with my rhythm',
        onPress: isFistStep ? () => setCurrentResultStepNumber(2) : goToNextStep,
        variant: 'contained',
        disabled: isLoading || !currentResultStep?.canGoNext,
      },
    ];

    return buttons;
  }, [currentResultStep, isLoading, goToNextStep, goToPreviousStep]);

  return (
    <OnboardingWrapper
      currentOnboardingStep={currentOnboardingStep}
      title={currentResultStep?.title!}
      buttons={renderButtons}
      onBack={goToPreviousStep}
      contentContainerStyle={{
        width: isMobile ? '100%' : '600px',
        marginTop: '40px',
        display: 'flex',
      }}
      buttonContainerStyle={{ flexDirection: 'column' }}
    >
      {renderContent()}
    </OnboardingWrapper>
  );
};

export default EnergyPredictionResultPage;
